'use client';

import React, { PropsWithChildren, useEffect, useRef } from 'react';
import { useLocale, useTranslations } from 'next-intl';

import { FaqListItemProp } from '@/services/';
import Image from 'next/image';
import icoArrowRight from '@images/ico_arrow_right.svg';
import { TrackedAnchor } from '@/components/ancher';
import { TrackedDetails } from '@/components/details';

import Pagination from './pagination';
import NoResult from './no-result';
import type { FaqCategory } from '../constants';

interface CategoryProps extends PropsWithChildren {
  active?: boolean;
  className?: string;
  category: string;
}

function CategoryLink({
  active,
  className,
  category,
  children,
}: CategoryProps) {
  const baseClassNames = `text-body2 block font-semibold rounded-lg text-3b-text-primary ${active ? 'md:text-3b-text-contrast' : 'md:text-3b-text-primary'} py-2 px-5 ${active ? 'bg-[#0000000d] md:bg-[#040435]' : 'md:bg-3b-background-light bg-3b-background-surface'}`;

  return (
    <TrackedAnchor
      href={`/faq/${category}`}
      className={`${baseClassNames} ${className} ${!active ? 'md:hover:bg-[#E4E6EB]' : ''}`}
      tabIndex={0}
      label={`category_${category}`}
      position="main_content"
    >
      {children}
    </TrackedAnchor>
  );
}

interface MainProps {
  categories: FaqCategory[];
  pageItems: FaqListItemProp[];
  pages: string[];
  totalCount: number;
  category: string;
  currentPage: number;
  type: 'category' | 'search';
  query?: string;
}

function Main({
  categories,
  pageItems,
  pages,
  totalCount,
  category,
  currentPage,
  type,
  query,
}: MainProps) {
  const t = useTranslations('Support.faq');
  const locale = useLocale();
  const headingRef = useRef<HTMLHeadingElement | null>(null);

  // 페이지 전환 시 키보드/스크린리더 사용자가 새 결과를 인지할 수 있도록 heading 으로 포커스 이동.
  useEffect(() => {
    if (currentPage > 1) {
      headingRef.current?.focus();
    }
  }, [currentPage]);

  const renderCategoryItem = (item: FaqCategory) => (
    <li key={item.value} className="whitespace-nowrap">
      <CategoryLink active={item.value === category} category={item.value}>
        {item.label}
      </CategoryLink>
    </li>
  );

  const showCategoryNav = type === 'category';
  const showPagination = pages.length > 0;
  const isEmpty = totalCount === 0;

  return (
    <nav
      aria-label={t('title')}
      className="flex flex-col gap-5 md:gap-8 lg:gap-10"
    >
      <h2
        ref={headingRef}
        tabIndex={-1}
        className="text-h5 font-semibold focus:outline-none md:text-h4"
      >
        {t('title')}
      </h2>

      {showCategoryNav && (
        <>
          <ul
            aria-live="polite"
            className="categories hidden w-[100wh] grow gap-4 overflow-auto pb-1 md:flex"
          >
            {categories.map(renderCategoryItem)}
          </ul>
          <TrackedDetails
            label="mobile_category"
            position="main_content"
            className="category open relative rounded-lg border border-solid border-3b-text-hint md:hidden"
          >
            <summary className="flex list-none items-center justify-between px-2.5 py-2.5">
              <span className="text-body2 font-semibold text-3b-text-primary">
                {
                  categories.find(
                    ({ value }: { value: string }) => value === category,
                  )?.label
                }
              </span>
              <div className="expand" />
            </summary>
            <ul
              aria-live="polite"
              className={`categories flex w-[100wh] flex-col gap-1 overflow-auto ${category !== 'all' ? 'border-t' : ''} border-dotted border-3b-text-hint`}
            >
              {categories.map(renderCategoryItem)}
            </ul>
          </TrackedDetails>
        </>
      )}

      <ul className="border-t border-solid border-gray-200">
        {pageItems.map((item, index) => (
          <li key={item.id ?? index}>
            <TrackedAnchor
              href={`/faq/item/${item.slug}`}
              label={`faq_item-${item.id}`}
              position="main_content"
              className="group flex justify-between gap-5 border-b border-solid border-gray-200 px-5 py-10 text-sh2 md:text-sh1"
            >
              <div className="flex items-center gap-5">
                <span className="font-semibold text-3b-text-secondary">Q</span>
                <span className="cursor-pointer font-semibold text-3b-text-primary group-hover:underline">
                  {item.title}
                </span>
              </div>
              <div className="flex items-center">
                <Image src={icoArrowRight} alt="" className="h-4 w-3" />
              </div>
            </TrackedAnchor>
          </li>
        ))}
      </ul>

      {showPagination ? (
        <Pagination
          type={type}
          category={category}
          pages={pages}
          page={String(currentPage)}
          query={query}
        />
      ) : isEmpty && type === 'search' && query ? (
        <NoResult locale={locale} query={`'${query}'`} />
      ) : null}
    </nav>
  );
}

export default Main;
